import React from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'

import { FormProvider, useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { DialogContent, Divider } from '@mui/material'

import T from 'T'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import { selectServiceName, selectPrice, selectContainerCount, selectExistingRentalFee } from 'slices/pricing/rentalFeeDialogSlice'

import SkeletonListItem from '../ListSkeleton'
import RentalFeeFormField from '../../common/field/RentalFeeFormField'
import RentalFeeServiceHeader from './RentalFeeServiceHeader'
import RentalFeesContentView from './RentalFeesContentView'
import useRentalFeeData from './useRentalFeeData'
import useRentalFeeActions from './useRentalFeeActions'

const RentalFeesDialogActionBar = ({ onClose = noop, onSuccessCallback = noop }) => {
  const { handleSubmit } = useFormContext()
  const { handleSave, isSaveLoading, isSaveDisabled } = useRentalFeeActions({ onClose, onSuccessCallback })

  return (
    <HHDialogActions>
      <CancelButton onClick={onClose} />
      <SaveButton
        loadingPosition="center"
        loading={isSaveLoading}
        disabled={isSaveDisabled}
        label={T.SAVE}
        onClick={handleSubmit(handleSave)}
      />
    </HHDialogActions>
  )
}

RentalFeesDialogActionBar.propTypes = {
  onClose: PropTypes.func,
  onSuccessCallback: PropTypes.func,
}

const RentalFeesDialogBody = ({ onClose = noop }) => {
  const { watch } = useFormContext()
  const rentalFee = watch('rentalFee')
  const existingRentalFee = useSelector(selectExistingRentalFee)
  const serviceName = useSelector(selectServiceName)
  const price = useSelector(selectPrice)
  const containerCount = useSelector(selectContainerCount)
  const { handleRentalFeeSelect } = useRentalFeeActions({ onClose })
  const isEditMode = Boolean(existingRentalFee)

  return (
    <>
      <RentalFeeServiceHeader serviceName={serviceName} price={price} containerCount={containerCount} />
      <Divider sx={{ my: 1 }} />
      <RentalFeeFormField
        name="rentalFeeSelector"
        value={rentalFee.rentalFeeId || ''}
        hideArchived
        disabled={isEditMode}
        onChange={handleRentalFeeSelect}
      />
      <RentalFeesContentView />
    </>
  )
}

RentalFeesDialogBody.propTypes = {
  onClose: PropTypes.func,
}

const RentalFeesDialogV2 = ({ isOpen = false, configuredServiceId, onClose, onSuccessCallback = noop }) => {
  const { form, isGetLoading } = useRentalFeeData({ configuredServiceId, enabled: isOpen, isDialog: true })

  return (
    <HHBaseDialog fullWidth maxWidth="md" open={isOpen} onClose={onClose}>
      <HHDialogTitle title={T.RENTAL_FEES} onClose={onClose} />
      <FormProvider {...form}>
        <DialogContent sx={{ px: 0 }}>
          {isGetLoading && <SkeletonListItem rows={1} />}
          {!isGetLoading && <RentalFeesDialogBody onClose={onClose} />}
        </DialogContent>
        <RentalFeesDialogActionBar onClose={onClose} onSuccessCallback={onSuccessCallback} />
      </FormProvider>
    </HHBaseDialog>
  )
}

RentalFeesDialogV2.propTypes = {
  isOpen: PropTypes.bool,
  configuredServiceId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onSuccessCallback: PropTypes.func,
}

export default RentalFeesDialogV2
